import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";

import { cn } from "../../lib/utils";

const buttonVariantsConfig = {
  variant: {
    default: "bg-primary text-primary-foreground",
    destructive: "bg-destructive text-destructive-foreground",
    outline: "border border-input bg-background",

    ghost: "",
    link: "text-primary underline-offset-4 hover:underline",
    primary:
      "bg-eraRed-600 text-white hover:bg-eraRed-hover active:bg-eraRed-active disabled:text-neutral-950 disabled:bg-eraRed-disabled",
    secondary:
      "bg-white text-black border border-black active:bg-stone-950 active:bg-opacity-20",
    tertiary:
      "text-eraRed-600 active:bg-eraRed-active active:bg-stone-950 active:bg-opacity-20",
  },
  size: {
    default: "h-10 px-4 py-2",
    sm: "h-9 px-3",
    lg: "h-11 px-8",
    icon: "h-10 w-10",
  },
} as const;

type ButtonVariant = keyof typeof buttonVariantsConfig.variant;
type ButtonSize = keyof typeof buttonVariantsConfig.size;

type ButtonVariantProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  class?: string;
};

type ButtonVariantFunction = (props?: ButtonVariantProps) => string;

export const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-3xl font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: buttonVariantsConfig,
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
) as ButtonVariantFunction;

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";
